// Service detail data — drives the per-service SEO landing pages.
import { CLIENT_PROJECTS } from "@/data/projects";

export const SERVICES = {
  "house-extensions-london": {
    slug: "house-extensions-london",
    name: "House Extensions",
    seoTitle: "House Extension Builders in London — Side-Return, Wrap-Around & Rear Extensions",
    metaDescription:
      "Premium house extension builders in London. Side-return, wrap-around, rear & double-storey extensions in Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead & Croydon. Free quote from Prime Build Design LTD.",
    h1: "House Extensions in London",
    intro:
      "From single-storey kitchen extensions to full double-storey side-returns, we design and build extensions that add space, value and light to your London home — completed on time, on budget and to a 10-year structural guarantee.",
    benefits: [
      "10+ years extending London homes — Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead, Croydon",
      "Full planning, party-wall & building-control management included",
      "Fixed-price contracts with weekly progress reports — no surprises",
      "In-house structural, joinery, MEP and finishing teams",
      "10-year structural guarantee on every extension we build",
    ],
    process: [
      ["01", "Discovery", "Free 45-min on-site or virtual consultation to understand your goals and budget."],
      ["02", "Design", "Concept design with feasibility, cost engineering and 3D visuals."],
      ["03", "Planning", "We handle planning, party-wall agreements and building-control submission."],
      ["04", "Build", "Weekly reports, in-house trades, locked budget — 12–20 weeks for most extensions."],
      ["05", "Handover", "Snagging walk-through, warranty pack and aftercare."],
    ],
    faqs: [
      { q: "How long does a house extension take to build?", a: "Most single-storey rear or side-return extensions take 12–16 weeks on site. Double-storey extensions take 16–20 weeks. We give you a firm programme before contract signing." },
      { q: "Do I need planning permission for a house extension?", a: "Many single-storey extensions fall under Permitted Development, but it depends on size, location and conservation status. We handle the assessment and full planning application if needed." },
      { q: "How much does a house extension cost in London?", a: "As an indicative 2026 range: £2,800–£3,800 per m² for a standard finish, £3,800–£5,200 per m² for premium, and £5,200+ per m² for luxury. Get a tailored AI estimate or book a free consultation for an accurate quote." },
      { q: "Do you handle the party wall agreement?", a: "Yes — for any work close to a shared wall we instruct a party-wall surveyor on your behalf and manage the agreements with neighbours from start to finish." },
    ],
    relatedProjectIds: ["ealing-completed-extension", "ealing-rear-extension", "wembley-side-return-opening", "chiswick-soldier-brickwork", "ealing-patio-install", "richmond-kitchen-finished-diner"],
    relatedServices: ["loft-conversions-london", "kitchen-renovations-london", "structural-work-london"],
    keywords: ["house extension builders London", "side return extension London", "wrap around extension", "rear extension contractors"],
  },

  "loft-conversions-london": {
    slug: "loft-conversions-london",
    name: "Loft Conversions",
    seoTitle: "Loft Conversion Specialists in London — Dormer, Mansard & L-Shape",
    metaDescription:
      "Loft conversion specialists in London. Dormer, mansard, hip-to-gable and L-shape loft conversions across Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead & Croydon. 10-year warranty.",
    h1: "Loft Conversions in London",
    intro:
      "Turn unused roof space into a master suite, home office or family room. We design and build dormer, mansard, hip-to-gable and L-shape loft conversions across London — from structural opening to finished ensuite.",
    benefits: [
      "Dormer, mansard, hip-to-gable & L-shape loft conversions",
      "Structural calculations, steel beams and full building-control coordination handled in-house",
      "Bespoke ensuite design (see our Richmond loft conversion case study)",
      "Average completion: 10–14 weeks from start to handover",
      "Adds an average 20–22% to UK home values",
    ],
    process: [
      ["01", "Survey", "Roof survey to confirm head-height, structural feasibility and conversion type."],
      ["02", "Design", "Loft layout, dormer/mansard design, ensuite planning and natural-light strategy."],
      ["03", "Permits", "Building control, party-wall agreements and planning (where needed) handled by us."],
      ["04", "Build", "Scaffold, structural openings, steels, joists, insulation, plasterboard, finishes — typically 10–14 weeks."],
      ["05", "Handover", "Sign-off, warranty and full snagging walk-through."],
    ],
    faqs: [
      { q: "How long does a loft conversion take?", a: "A standard dormer takes 10–12 weeks. A mansard or L-shape conversion takes 12–14 weeks. Our Richmond loft conversion (full master suite + ensuite + family bathroom) was completed in 12 weeks." },
      { q: "Do I need planning permission for a loft conversion?", a: "Many dormer and hip-to-gable conversions fall under Permitted Development. Mansards usually require planning. We confirm at the survey stage and handle the application if required." },
      { q: "How much does a loft conversion cost in London?", a: "As an indicative 2026 range: £55k–£75k for a Velux conversion, £70k–£105k for a dormer, £95k–£145k for a mansard or L-shape with ensuite." },
      { q: "Will I need to move out during the loft conversion?", a: "No. Most of the work is contained to the loft and accessed via scaffold. We seal the staircase opening until the very end." },
    ],
    relatedProjectIds: ["richmond-loft-ensuite-wide", "richmond-loft-shower", "richmond-loft-vanity", "richmond-loft-master-bedroom", "richmond-loft-bedroom", "richmond-loft-family-bathroom-wide"],
    relatedServices: ["bathroom-renovations-london", "structural-work-london", "house-extensions-london"],
    keywords: ["loft conversion specialists London", "dormer loft conversion London", "mansard loft conversion", "loft conversion bathroom"],
  },

  "kitchen-renovations-london": {
    slug: "kitchen-renovations-london",
    name: "Kitchen Renovations",
    seoTitle: "Kitchen Renovations in London — Bespoke Kitchens & Kitchen Extensions",
    metaDescription:
      "Bespoke kitchen renovations and kitchen-extension specialists in London. Shaker, handle-less and bespoke joinery kitchens across Ealing, Chiswick, Richmond, Wimbledon, Hampstead, Croydon.",
    h1: "Kitchen Renovations in London",
    intro:
      "From a fresh kitchen fit-out to a full open-plan kitchen-diner extension with bifold doors and underfloor heating — we design, build and finish kitchens that will define your home for the next decade.",
    benefits: [
      "Bespoke Shaker, handle-less and in-frame kitchens — design + build",
      "In-house joinery for islands, larders and bespoke storage",
      "Integration with extensions, structural openings and underfloor heating",
      "Premium brand specs: Miele, Wolf, Sub-Zero, Quooker, Caesarstone, Silestone",
      "8–12 weeks from strip-out to finished kitchen",
    ],
    process: [
      ["01", "Brief", "Site visit, lifestyle questions and design brief — usually 60–90 mins."],
      ["02", "Design", "Layout, materials, lighting and appliance schedule — 2-3 design rounds included."],
      ["03", "Order", "Joinery, stone, appliances and finishes ordered with lead-times locked."],
      ["04", "Build", "Strip-out, MEP rough-in, joinery install, stone template & fit, second fix, snag."],
      ["05", "Handover", "Walk-through, care guide and aftercare visit at 3 months."],
    ],
    faqs: [
      { q: "How long does a kitchen renovation take?", a: "A direct-replacement kitchen takes 4–6 weeks. A kitchen with structural changes or extension takes 10–16 weeks." },
      { q: "How much does a bespoke kitchen cost in London?", a: "Standard fit-outs from £35k–£60k. Premium bespoke kitchens £60k–£120k. Luxury kitchens with imported appliances and bespoke joinery £120k+." },
      { q: "Can you extend our kitchen and build it in one contract?", a: "Yes. Kitchen extensions are our largest single project type — see our Richmond Kitchen Extension case study for an open-plan kitchen-diner with bifolds and herringbone parquet." },
    ],
    relatedProjectIds: ["richmond-kitchen-finished-diner", "richmond-kitchen-range-detail", "richmond-kitchen-island-bifold", "richmond-kitchen-island-pendants", "richmond-kitchen-bifold-view", "chiswick-kitchen-fitout", "richmond-kitchen-island-fridge"],
    relatedServices: ["house-extensions-london", "full-house-renovations-london", "bathroom-renovations-london"],
    keywords: ["kitchen renovation London", "bespoke kitchen London", "kitchen extension London", "open plan kitchen London"],
  },

  "bathroom-renovations-london": {
    slug: "bathroom-renovations-london",
    name: "Bathroom Renovations",
    seoTitle: "Bathroom Renovations in London — Ensuite, Walk-In Shower & Loft Bathrooms",
    metaDescription:
      "Premium bathroom renovations across London. Walk-in showers, ensuite refurbishments and loft bathrooms in Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead, Croydon.",
    h1: "Bathroom Renovations in London",
    intro:
      "Whether it's a single ensuite refurb or a complete loft bathroom carved into the eaves, we handle every stage — first-fix plumbing, tiling, sanitaryware, lighting and bespoke joinery — to a hotel-suite standard.",
    benefits: [
      "Full ensuite, family bathroom and loft bathroom design + build",
      "Marble, porcelain large-format, metro and natural-stone tiling specialists",
      "In-house first-fix plumbing, electrics and tiling teams",
      "Premium brassware: Crosswater, Hansgrohe, Lefroy Brooks, JTP",
      "Average completion: 3–5 weeks per bathroom",
    ],
    process: [
      ["01", "Design", "Layout, fixture schedule, tile spec, lighting strategy."],
      ["02", "Strip-out", "Old suite removed, structure prepped, waste route confirmed."],
      ["03", "First fix", "Plumbing, electrics, waterproofing tanking — building-control sign-off where needed."],
      ["04", "Tile + fit", "Tiling, sanitaryware, brassware install, second fix."],
      ["05", "Snag", "Silicone lines, glass installed, walk-through and warranty pack."],
    ],
    faqs: [
      { q: "How long does a bathroom renovation take?", a: "A standard family bathroom takes 3–4 weeks. A loft bathroom with sloped ceilings, marble tiling and bespoke joinery takes 4–6 weeks." },
      { q: "How much does a bathroom cost in London?", a: "Standard refurbs from £12k–£22k. Premium ensuites £22k–£40k. Luxury bathrooms with marble, bespoke vanity and underfloor heating £40k–£75k." },
      { q: "Can you do a loft ensuite?", a: "Yes — loft bathrooms are a speciality. See our Richmond Loft Conversion case study with master marble ensuite + family navy-tile bathroom." },
    ],
    relatedProjectIds: ["richmond-loft-ensuite-wide", "richmond-loft-shower", "richmond-loft-vanity", "richmond-loft-family-bathroom-wide", "richmond-loft-family-bathroom-bath", "richmond-bathroom-refurb"],
    relatedServices: ["loft-conversions-london", "full-house-renovations-london", "kitchen-renovations-london"],
    keywords: ["bathroom renovation London", "ensuite renovation London", "loft bathroom London", "walk in shower London"],
  },

  "full-house-renovations-london": {
    slug: "full-house-renovations-london",
    name: "Full House Renovations",
    seoTitle: "Full House Renovations in London — Whole-Home Refurbishment Specialists",
    metaDescription:
      "Whole-home renovation specialists in London. From strip-out to finished home including structural, MEP, joinery, kitchens and bathrooms. Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead, Croydon.",
    h1: "Full House Renovations in London",
    intro:
      "A whole-home renovation is the deepest, most transformative project we deliver — and the one we love most. From strip-out and structural openings through MEP, joinery, kitchens, bathrooms and finishing, you get one accountable team and one contract.",
    benefits: [
      "End-to-end whole-home delivery under a single contract",
      "Single project lead, weekly reports, one fixed budget",
      "In-house structural, MEP, joinery and finishing — no chains of sub-contractors",
      "Average whole-home project: 4–7 months",
      "10-year structural guarantee + 2-year finishes warranty",
    ],
    process: [
      ["01", "Brief & Design", "Concept, scope, fixture schedule and cost engineering — 2–6 weeks."],
      ["02", "Permits", "Planning, party-wall, building-control — handled by us."],
      ["03", "Strip-out", "Soft-strip, hard-strip, structural openings, MEP first fix."],
      ["04", "Build", "Plasterboard, screed, joinery, tiling, kitchens, bathrooms, decoration."],
      ["05", "Handover", "Snagging, warranty, care guide and a 6-month aftercare check-in."],
    ],
    faqs: [
      { q: "How long does a full house renovation take?", a: "A standard 3-bed renovation takes 16–20 weeks. A larger whole-home with structural alterations and loft conversion takes 22–32 weeks." },
      { q: "Will I need to move out during a full renovation?", a: "For a full strip-out renovation, yes — usually for 12+ weeks. For partial works we can sometimes stage the project to keep one floor habitable." },
      { q: "How much does a full house renovation cost in London?", a: "A typical UK whole-home renovation runs £180k–£400k for a 3-bed, £400k–£800k+ for a 4-5 bed including kitchen, bathrooms, structural and finishing. See our Richmond whole-home case study." },
    ],
    relatedProjectIds: ["wp-renovation-1", "wp-ufh-2", "richmond-reception-navy", "richmond-blue-bedroom", "richmond-guest-bedroom-wardrobes", "wimbledon-staircase", "chiswick-kitchen-fitout"],
    relatedServices: ["kitchen-renovations-london", "bathroom-renovations-london", "loft-conversions-london"],
    keywords: ["full house renovation London", "whole home renovation London", "renovation company London", "luxury renovation London"],
  },

  "new-build-contractors-london": {
    slug: "new-build-contractors-london",
    name: "New Build Contractors",
    seoTitle: "New Build Contractors in London — Domestic New Builds, Foundations to Handover",
    metaDescription:
      "Domestic new build contractors in London. Ground-up residential new builds with full structural, MEP, joinery and finishing. Hampstead, Wimbledon, Richmond, Croydon and across London.",
    h1: "New Build Contractors in London",
    intro:
      "Ground-up domestic new builds, from the first site survey and foundations through to handover. We work with your architect and design team or our own design partners — delivered with the same rigour, transparency and craftsmanship as every Prime Build project.",
    benefits: [
      "Domestic new builds for private clients & investors",
      "Foundations, frame, brickwork, MEP, joinery, finishing — all in-house",
      "Cavity-wall and traditional masonry build-up specialists",
      "Buff brick, London stock and bespoke corbel detail capable",
      "Typical new-build programme: 9–18 months",
    ],
    process: [
      ["01", "Feasibility", "Site, budget, planning context — go/no-go in 2 weeks."],
      ["02", "Design", "Architect-led or our design partners; structural, MEP and detail design."],
      ["03", "Permits", "Planning, building control, party-wall, demolition notices."],
      ["04", "Build", "Ground-works → frame → MEP → finishing — monthly client meetings."],
      ["05", "Handover", "Building Regs sign-off, snagging, certificates and warranty."],
    ],
    faqs: [
      { q: "Do you take on new-build domestic projects?", a: "Yes — see our Hampstead New Build case study showing structural shell, cavity wall, buff brick facade and corbelled brick detail." },
      { q: "Can you work with our architect?", a: "Yes, we frequently work with private clients' architects. We also offer design-build under a single contract if that's preferred." },
      { q: "How much does a new build cost in London?", a: "Domestic new builds in London typically run £3,200–£4,800 per m² for standard, £4,800–£6,500 per m² for premium, and £6,500+ per m² for luxury including finishes." },
    ],
    relatedProjectIds: ["hampstead-newbuild-buff-facade", "hampstead-newbuild-brick-detail", "hampstead-newbuild-cavity", "hampstead-newbuild-shell-joists"],
    relatedServices: ["structural-work-london", "full-house-renovations-london", "house-extensions-london"],
    keywords: ["new build contractors London", "domestic new build London", "residential new build builder"],
  },

  "structural-work-london": {
    slug: "structural-work-london",
    name: "Structural Work",
    seoTitle: "Structural Work in London — Steel Beams, Load-Bearing Removals, Underpinning",
    metaDescription:
      "Specialist structural work across London — steel beam installations, load-bearing wall removals, underpinning, structural openings and chimney removal. Fully insured, building-control compliant.",
    h1: "Structural Work in London",
    intro:
      "The hardest part of any renovation is rarely visible once it's finished — the steel beams, structural openings, underpinning and load redistribution that make modern open-plan living possible. We've been doing it for 10 years across London.",
    benefits: [
      "Steel beam (RSJ) installations to engineer's specification",
      "Load-bearing wall removals & structural openings",
      "Underpinning, foundation strengthening and basement excavation",
      "Chimney removals, breast removals and re-supporting",
      "Building Control sign-off included on every job",
    ],
    process: [
      ["01", "Survey", "On-site assessment + structural engineer's calculations."],
      ["02", "Design", "Steel schedule, propping plan and Building-Control submission."],
      ["03", "Prep", "Propping, structural support, area isolation."],
      ["04", "Install", "Steel install, pad-stones, re-supporting, plate work."],
      ["05", "Sign-off", "Building Control inspection, certificate issued, structure released."],
    ],
    faqs: [
      { q: "Do I need Building Control for a steel beam?", a: "Yes — any change to a load-bearing structure needs Building Control sign-off, which we manage end-to-end." },
      { q: "Can you remove an internal load-bearing wall?", a: "Yes. We work with a structural engineer to calculate the required steel, install pad-stones, prop the floor above and install the RSJ with full Building Control approval." },
      { q: "Do you do underpinning?", a: "Yes — we underpin existing foundations for basement excavations, settlement remediation and structural strengthening. All with insurance and certification." },
    ],
    relatedProjectIds: ["hampstead-extension-steel-joists", "chiswick-steel-firstfix", "wembley-side-return-opening", "hampstead-newbuild-cavity"],
    relatedServices: ["house-extensions-london", "loft-conversions-london", "new-build-contractors-london"],
    keywords: ["structural work London", "steel beam installation London", "load bearing wall removal London", "underpinning London"],
  },
};

// Helper to resolve projects for a service
export function projectsForService(slug) {
  const svc = SERVICES[slug];
  if (!svc) return [];
  return svc.relatedProjectIds
    .map((id) => CLIENT_PROJECTS.find((p) => p.id === id))
    .filter(Boolean);
}

export const SERVICE_SLUGS = Object.keys(SERVICES);
